export const optimizeCanvasState = (canvasState) => {
    if (!canvasState || !canvasState.images || !Array.isArray(canvasState.images)) {
        return canvasState;
    }

    // Deep copy to avoid mutating the original state
    const optimizedState = JSON.parse(JSON.stringify(canvasState));
    const srcToIdMap = new Map();

    // Iterate through images to identify duplicates.

    optimizedState.images = optimizedState.images.map(image => {
        // If it's not a data URL or valid source, just return it
        if (!image.src || typeof image.src !== 'string') {
            return image;
        }

        // Check if we've seen this src before
        if (srcToIdMap.has(image.src)) {
            return {
                ...image,
                src: srcToIdMap.get(image.src)
            };
        } else {
            if (image.id) {
                srcToIdMap.set(image.src, image.id);
            }
            return image;
        }
    });

    return optimizedState;
};

export const reconstructCanvasState = (canvasState) => {
    if (!canvasState || !canvasState.images || !Array.isArray(canvasState.images)) {
        return canvasState;
    }

    // Deep copy to avoid mutating the input if it's being used elsewhere immediately
    const reconstructedState = JSON.parse(JSON.stringify(canvasState));
    const idToSrcMap = new Map();

    reconstructedState.images.forEach(image => {
        if (image.id && image.src && image.src !== image.id) {
            if (image.src.length > 50 || image.src.startsWith('data:') || image.src.startsWith('http') || image.src.startsWith('/')) {
                idToSrcMap.set(image.id, image.src);
            }
        }
    });

    reconstructedState.images = reconstructedState.images.map(image => {
        if (idToSrcMap.has(image.src)) {
            return {
                ...image,
                src: idToSrcMap.get(image.src)
            };
        }
        return image;
    });

    return reconstructedState;
};

// Resolve what `oldColorId` (a color of fromFabric) becomes on toFabric,
// falling back to toFabric's default paint color when there's no mapping.
export const resolveSubstituteColorId = (fromFabric, oldColorId, toFabric) => {
    const substituted = oldColorId != null
        ? fromFabric.substitutes?.[oldColorId]?.[toFabric.id]
        : undefined;
    return substituted ?? toFabric.colors.defaultPaint;
};

// Remap every non-nomex cell of envelopeGrid (hex strings) from fromFabric's
// palette to toFabric's palette. Nomex rows (row < nomexBottomRows) are
// returned unchanged, since every fabric shares the same nomex palette.
export const remapEnvelopeGridColors = (envelopeGrid, nomexBottomRows, fromFabric, toFabric) => {
    const hexToId = {};
    fromFabric.colors.items.forEach(color => { hexToId[color.hex] = color.id; });
    const idToHex = {};
    toFabric.colors.items.forEach(color => { idToHex[color.id] = color.hex; });
    const fallbackHex = idToHex[toFabric.colors.defaultPaint];

    return envelopeGrid.map((row, rowIdx) => {
        if (rowIdx < nomexBottomRows) {
            return row;
        }
        return row.map(hex => {
            if (!hex) {
                return hex;
            }
            const newId = resolveSubstituteColorId(fromFabric, hexToId[hex], toFabric);
            return idToHex[newId] ?? fallbackHex;
        });
    });
};

export const toBase64 = async (url) => {
    if (!url || typeof url !== 'string' || url.startsWith('data:')) {
        return url;
    }
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Error converting to base64:", error);
        return url; // Fallback to original URL
    }
};
